import React, { ReactNode, useCallback, useMemo, forwardRef, useState } from 'react'
import type { FieldError } from 'react-hook-form'

import { cn } from '@/lib/utils'
import Icon, { IconTypes } from '@/components/Icon'
import LcIcon, { LcIconName } from '@/components/livecontact/icons'

/**
 * Figma text field: white, radius 8, `0 0 0 1px #E9E9E9` ring + 4px soft drop; brand ring on focus.
 * Callers still override sizing through `classInput` (tailwind-merge resolves the conflicts).
 */
const FIELD_BASE =
  'w-full rounded-[8px] bg-surface px-3 text-[14px] leading-[1.4] tracking-[-0.2px] text-ink shadow-input outline-none ' +
  'transition-[box-shadow,background-color] duration-[160ms] ease-out-strong placeholder:text-ink-4 ' +
  'focus:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.14)] ' +
  'read-only:bg-canvas'
const FIELD_ERROR =
  'shadow-[0_0_0_1px_#EF4444,0_0_0_4px_rgba(239,68,68,0.12)] focus:shadow-[0_0_0_1px_#EF4444,0_0_0_4px_rgba(239,68,68,0.12)]'


/** Legacy field icon names that have a design-system glyph; anything else falls back to the old sprite. */
const LC_FIELD_ICON: Partial<Record<string, LcIconName>> = {
  email: 'mail',
  mail: 'mail',
  search: 'search',
  calendar: 'calendar',
  phone: 'phone',
  call: 'call',
  link: 'link',
  user: 'user',
  profile: 'user',
  web: 'globe',
  globe: 'globe',
  eye: 'eye',
  key: 'key',
  lock: 'key',
  tag: 'tag',
  tags: 'tags',
  clock: 'clock',
  at: 'at',
  'credit-card': 'credit-card',
  card: 'credit-card',
  building: 'user-circle',
}

type FieldProps = {
  className?: string
  classInput?: string
  classLabel?: string
  classInnerInput?: string
  label?: ReactNode
  textarea?: boolean
  note?: string
  type?: string
  value?: string
  defaultValue?: string
  onChange?: (value: any) => void
  onBlur?: (event: any) => void
  onClick?: (event: any) => void
  onFocus?: (event: any) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  showError?: boolean
  icon?: IconTypes
  error?: FieldError
  classLabelInner?: string
  readOnly?: boolean
  appendComponent?: () => ReactNode
  rows?: number
  autoFocus?: boolean
  step?: number
  charactersLimit?: number
  visibleRemainingChars?: boolean
  classIcon?: string
  inputStyle?: React.CSSProperties
  /** Standard input attributes callers need for labelling, autofill and mobile keyboards. */
  id?: string
  name?: string
  autoComplete?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  maxLength?: number
  minLength?: number
  min?: number | string
  max?: number | string
  pattern?: string
  spellCheck?: boolean
  'aria-label'?: string
  'aria-describedby'?: string
}

const Field = forwardRef<HTMLInputElement | HTMLTextAreaElement, FieldProps>(
  (
    {
      className,
      classInput,
      classLabel,
      classInnerInput,
      label,
      textarea,
      note,
      type,
      value,
      defaultValue,
      onChange,
      onKeyDown,
      onBlur,
      onFocus,
      placeholder,
      required,
      readOnly = false,
      rows = 3,
      disabled,
      onClick,
      icon,
      step,
      error,
      autoFocus,
      appendComponent = () => null,
      classLabelInner,
      charactersLimit = 2500,
      visibleRemainingChars = true,
      classIcon,
      inputStyle = {},
      showError = true,
      id,
      name,
      autoComplete,
      inputMode,
      maxLength,
      minLength,
      min,
      max,
      pattern,
      spellCheck,
      ...aria
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false)

    const isPassword = useMemo(() => type === 'password', [type])
    const remainingChars = useMemo(
      () => charactersLimit - (value?.length ?? 0),
      [charactersLimit, value?.length]
    )

    const inputType = useMemo(() => {
      if (!isPassword) return type

      return isPasswordVisible ? 'text' : 'password'
    }, [isPassword, isPasswordVisible, type])

    const handleKeyDown = useCallback(
      (event: any) => {
        // Handle character limit for textareas
        if (textarea) {
          const remainingChars = charactersLimit - (value?.length ?? 0)
          if (remainingChars <= 0 && event.key !== 'Backspace') {
            event.preventDefault()
            return
          }
        }

        // Call custom onKeyDown if provided
        if (onKeyDown) {
          onKeyDown(event)
        }
      },
      [onKeyDown, textarea, value?.length, charactersLimit]
    )

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let val: string | number = event.target.value
        if (type === 'number') {
          val = val === '' ? '' : Number(val)
        }
        onChange?.(val)
      },
      [onChange, type]
    )
    const handleTogglePassword = useCallback(() => {
      setIsPasswordVisible((prev) => !prev)
    }, [])

    const nativeProps = {
      id,
      name,
      autoComplete,
      inputMode,
      maxLength,
      minLength,
      spellCheck,
      'aria-label': aria['aria-label'],
      'aria-describedby': aria['aria-describedby'],
    }

    return (
      <div className={cn(className)}>
        {label && (
          <div
            className={cn(
              'mb-1.5 flex items-baseline text-[12px] font-medium leading-[1.4] tracking-[-0.2px] text-ink-3',
              classLabelInner
            )}
          >
            <span className={classLabel}>{label}</span>
            {textarea && visibleRemainingChars ? (
              <span className="ml-auto pl-4 tabular-nums text-ink-4">{remainingChars} chars</span>
            ) : null}
          </div>
        )}
        <div className={cn('relative', classInnerInput)}>
          {textarea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              className={cn(
                FIELD_BASE,
                'min-h-24 resize-none py-2.5',
                icon && 'pl-10',
                classInput,
                disabled && 'cursor-not-allowed opacity-60',
                error && FIELD_ERROR
              )}
              value={value}
              defaultValue={defaultValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              rows={rows}
              aria-invalid={!!error}
              autoFocus={autoFocus}
              style={inputStyle}
              {...nativeProps}
            ></textarea>
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              className={cn(
                FIELD_BASE,
                'h-9',
                icon && 'pl-10',
                isPassword && 'pr-10',
                disabled && 'cursor-not-allowed opacity-60',
                error && FIELD_ERROR,
                classInput
              )}
              type={inputType || 'text'}
              value={value}
              onChange={handleChange}
              onBlur={onBlur}
              onClick={onClick}
              onFocus={onFocus}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              onKeyDown={handleKeyDown}
              aria-invalid={!!error}
              defaultValue={defaultValue}
              autoFocus={autoFocus}
              step={step}
              min={min}
              max={max}
              pattern={pattern}
              style={inputStyle}
              {...nativeProps}
            />
          )}
          {icon && (
            <span
              className={cn(
                'pointer-events-none absolute left-3 top-1/2 inline-flex size-4 -translate-y-1/2 items-center justify-center text-ink-4 transition-colors duration-[160ms]',
                value !== '' && 'text-ink-2',
                classIcon
              )}
            >
              {LC_FIELD_ICON[icon] ? (
                <LcIcon name={LC_FIELD_ICON[icon]!} />
              ) : (
                <Icon className="size-4 fill-current" name={icon} />
              )}
            </span>
          )}
          {isPassword && !textarea && (
            <button
              type="button"
              onClick={handleTogglePassword}
              disabled={disabled}
              tabIndex={-1}
              className={cn(
                'group absolute right-3 top-1/2 size-4 -translate-y-1/2 text-ink-4 transition-colors hover:text-ink-2 disabled:cursor-not-allowed disabled:opacity-50',
                isPasswordVisible &&
                  'before:absolute before:z-[1] before:top-1/2 before:left-1/2 before:w-[2px] before:h-5 before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:rounded-full before:bg-current'
              )}
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
              aria-pressed={isPasswordVisible}
            >
              <LcIcon name="eye" className="absolute left-0 top-0" />
            </button>
          )}
          {appendComponent()}
        </div>
        {note && (
          <div className="mt-1.5 text-[12px] leading-[1.4] tracking-[-0.2px] text-ink-4">{note}</div>
        )}
        {showError && error?.message && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[12px] leading-[1.4] tracking-[-0.2px] text-[#D33] animate-lc-fade-up">
            <LcIcon name="info" size={13} className="shrink-0" />
            <span>{error.message}</span>
          </div>
        )}
      </div>
    )
  }
)

Field.displayName = 'Field'

export default Field
